// ════════════════════════════════════════════════════════════════════
// Glass-i — Sales Tax Invoice API client (Phase 1B).
// Mirrors proformaApi.js (shared axios instance + {success,data} unwrap), but
// points at /api/erp/sales/invoices. Master loaders (customers / salespersons /
// items / dropdowns) are mode-agnostic, so they are re-exported from proformaApi
// rather than duplicated.
//
// post() finalises a DRAFT to the ledger (Phase 2). cancel() reverses a POSTED
// invoice's accounting voucher (Phase 2C) — a DISTINCT action from discard()
// (which is draft-only). Neither makes any GST-portal / IRN / e-Way call.
// ════════════════════════════════════════════════════════════════════

import client from '../../../../api/client';

const BASE = '/api/erp/sales/invoices';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined ? resp.data.data : (resp ? resp.data : null);
}

export const invoiceApi = {
  list: (params = {}) => client.get(BASE, { params }).then(unwrap),
  get: (id) => client.get(`${BASE}/${encodeURIComponent(id)}`).then(unwrap),
  create: (payload = {}) => client.post(BASE, payload).then(unwrap),
  update: (id, payload = {}) => client.put(`${BASE}/${encodeURIComponent(id)}`, payload).then(unwrap),
  preview: (payload = {}) => client.post(`${BASE}/preview`, payload).then(unwrap),
  events: (id) => client.get(`${BASE}/${encodeURIComponent(id)}/events`).then(unwrap),
  // ── import picker: part deliveries (migration 0186) ──────────────
  // Parent grid - confirmed orders with something left to bill.
  importCandidates: (params = {}) =>
    client.get(`${BASE}/import-candidates`, { params }).then(unwrap),
  // Child grid - per-line ordered / ready / dispatched / invoiced / pending.
  importLines: (proformaId) =>
    client.get(`${BASE}/import-candidates/${encodeURIComponent(proformaId)}/lines`).then(unwrap),

  // Create a DRAFT from a CONFIRMED proforma. Pass `lines: [{proformaLineId, qty}]`
  // to bill only part of the order; omit it and the whole order is imported, which
  // is what every caller before 0186 did. The backend refuses, per line, to bill
  // more than was ordered - the client never has to enforce that itself.
  importFromProforma: (proformaId, payload = {}) =>
    client.post(`${BASE}/import-proforma/${encodeURIComponent(proformaId)}`, payload).then(unwrap),
  // Abandon a DRAFT (draft-only; frees the source proforma). Not a delete.
  discard: (id, reason) =>
    client.post(`${BASE}/${encodeURIComponent(id)}/discard`, { reason }).then(unwrap),
  // Finalise a DRAFT to the ledger (Phase 2 GL posting). Backend is authoritative
  // (creates/reuses the accounting voucher); the client sends no voucher data.
  post: (id) =>
    client.post(`${BASE}/${encodeURIComponent(id)}/post`).then(unwrap),
  // Cancel a POSTED invoice — reverses the accounting voucher (Phase 2C). Reason
  // is mandatory. Draft must use discard() instead.
  cancel: (id, reason) =>
    client.post(`${BASE}/${encodeURIComponent(id)}/cancel`, { reason }).then(unwrap)
};

// Master loaders are shared with proforma (customers / salespersons / items /
// dropdowns are the same masters) — re-export so invoice screens reuse them.
export {
  loadCustomersSafe, loadCustomerByIdSafe, loadSalespersonsSafe,
  loadItemsSafe, loadProformaDropdownsSafe
} from '../proforma/proformaApi';

export default invoiceApi;
